/**
 * Resolve a persisted `overlay` block into render-ready choropleth data (M9-B).
 *
 * The `map.style.overlay` block carries only the SELECTION; the boundary
 * geometry + values + classification are resolved from the overlay engine at
 * render time. `resolveOverlayData` is the ONE place that resolution happens, so
 * the editor and the M6 embed feed MapCanvas byte-identical inputs for the same
 * `{overlay block, engine, tokens}` — the deterministic-redraw contract. Pure
 * control-flow over the injected engine (fixture in tests, pg-backed at deploy).
 */

import type { MapTokens } from "@/lib/map";
import type { BBox, OverlayEngine } from "./engine";
import type { OverlaySpec } from "./spec";
import type { Boundary, Choropleth } from "./types";

/** Render inputs the M9-A choropleth layer needs (assignable to MapCanvas `OverlayRenderData`). */
export interface ResolvedOverlayData {
  boundaries: Boundary[];
  values: Map<string, number>;
  choropleth: Choropleth;
}

export interface ResolveOverlayOptions {
  /** Optional viewport clip — the engine returns only intersecting boundaries. */
  bbox?: BBox;
}

/**
 * Resolve `{boundaries, values, choropleth}` for an overlay selection. Returns
 * null when the selection yields nothing renderable (no boundaries, or a
 * classification with no classes) so the caller leaves the choropleth layer
 * inert. Throws only if the country's provider is unconfigured (a real
 * misconfiguration), matching the engine's contract.
 */
export async function resolveOverlayData(
  engine: OverlayEngine,
  overlay: OverlaySpec,
  tokens: MapTokens,
  opts: ResolveOverlayOptions = {},
): Promise<ResolvedOverlayData | null> {
  const common = {
    country: overlay.country,
    variableId: overlay.variableId,
    tier: overlay.tier,
    ...(overlay.vintage ? { vintage: overlay.vintage } : {}),
  };
  const [boundaries, values, choropleth] = await Promise.all([
    engine.getBoundaries({
      country: overlay.country,
      tier: overlay.tier,
      ...(opts.bbox ? { bbox: opts.bbox } : {}),
      ...(overlay.vintage ? { vintage: overlay.vintage } : {}),
    }),
    engine.getValues(common),
    engine.getChoropleth({
      ...common,
      classification: { ...overlay.classification, rampTokenId: overlay.rampTokenId },
      tokens,
    }),
  ]);
  if (boundaries.length === 0 || choropleth.stops.length === 0) return null;
  return { boundaries, values, choropleth };
}
